import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, Modal, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  useChartOfAccounts,
  useCreateAccount,
  useUpdateAccount,
  useAccountingCtx,
  type Account,
  type AccountInput,
  ACCOUNT_TYPE_LABEL,
  accountTypeBadgeVariant,
} from './useAccounting';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'asset', label: 'Ativo' },
  { value: 'liability', label: 'Passivo' },
  { value: 'equity', label: 'Patrimonio Liquido' },
  { value: 'revenue', label: 'Receita' },
  { value: 'expense', label: 'Despesa' },
];

/* ── Tree node component ── */

function AccountTreeNode({
  account,
  depth,
  onEdit,
}: {
  account: Account;
  depth: number;
  onEdit: (acc: Account) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = account.children && account.children.length > 0;

  return (
    <>
      <tr
        className="chart-row"
        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
        onClick={() => { if (hasChildren) setExpanded(!expanded); }}
      >
        <td
          style={{
            padding: '0.625rem 1rem',
            borderBottom: '1px solid #eceef0',
            paddingLeft: `${1 + depth * 1.5}rem`,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
            {hasChildren && (
              <span
                className="material-symbols-outlined text-on-surface-variant"
                style={{ fontSize: '18px', width: '18px', display: 'inline-block' }}
              >
                {expanded ? 'expand_more' : 'chevron_right'}
              </span>
            )}
            <span style={{ fontWeight: depth === 0 ? 600 : 400 }}>{account.code}</span>
          </span>
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          {account.name}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          <Badge variant={accountTypeBadgeVariant(account.type)} size="sm">
            {ACCOUNT_TYPE_LABEL[account.type]}
          </Badge>
        </td>
        <td
          className="font-label-md text-label-md text-on-surface-variant"
          style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}
        >
          {NATURE_BY_TYPE[account.type]}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          <Badge variant={isSynthetic(account) ? 'neutral' : 'info'} size="sm">
            {isSynthetic(account) ? 'Sint\u00e9tica' : 'Anal\u00edtica'}
          </Badge>
        </td>
        <td
          className="font-label-md text-label-md"
          style={{
            padding: '0.625rem 1rem',
            borderBottom: '1px solid #eceef0',
            textAlign: 'right',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatCurrency(account.balance)}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
          <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEdit(account); }}>
            Editar
          </Button>
        </td>
      </tr>
      {expanded && hasChildren &&
        account.children!.map((child) => (
          <AccountTreeNode key={child.id} account={child} depth={depth + 1} onEdit={onEdit} />
        ))}
    </>
  );
}

/*
  Natureza da conta: convencao contabil aplicada ao `type` que o backend ja
  devolve. Ativo e despesa tem natureza devedora; passivo, patrimonio liquido e
  receita, credora. Nao e campo novo, e leitura do que existe.
*/
const NATURE_BY_TYPE: Record<Account['type'], 'D\u00e9bito' | 'Cr\u00e9dito'> = {
  asset: 'D\u00e9bito',
  expense: 'D\u00e9bito',
  liability: 'Cr\u00e9dito',
  equity: 'Cr\u00e9dito',
  revenue: 'Cr\u00e9dito',
};

/* Conta sintetica e, por definicao, a que tem contas filhas. */
function isSynthetic(account: Account): boolean {
  return !!account.children && account.children.length > 0;
}

/* ── Account Form Modal ── */

function AccountFormModal({
  isOpen,
  onClose,
  account,
  accounts,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  account?: Account;
  accounts: Account[];
  onSubmit: (data: AccountInput) => Promise<void>;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('asset');
  const [parentId, setParentId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (account) {
      setCode(account.code);
      setName(account.name);
      setType(account.type);
      setParentId(account.parentId ?? '');
    } else {
      setCode('');
      setName('');
      setType('asset');
      setParentId('');
    }
    setError(null);
  }, [account, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code.trim()) { setError('Código da conta e obrigatório.'); return; }
    if (!name.trim()) { setError('Nome da conta e obrigatório.'); return; }

    try {
      setSubmitting(true);
      await onSubmit({
        code: code.trim(),
        name: name.trim(),
        type: type as Account['type'],
        parentId: parentId || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar conta.');
    } finally {
      setSubmitting(false);
    }
  };

  /* Flatten accounts for parent dropdown */
  function flattenAccounts(list: Account[], level = 0): { value: string; label: string }[] {
    const result: { value: string; label: string }[] = [];
    for (const a of list) {
      result.push({ value: a.id, label: `${' '.repeat(level * 2)}${a.code} - ${a.name}` });
      if (a.children) result.push(...flattenAccounts(a.children, level + 1));
    }
    return result;
  }

  const parentOptions = flattenAccounts(accounts);
  const title = account ? 'Editar Conta' : 'Nova Conta';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Código" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Ex: 1.1.1" />
          <Input label="Nome" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ex: Caixa" />
          <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value)}>
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          <Select label="Conta pai (opcional)" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">-- Nenhuma --</option>
            {parentOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          {error && <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>{error}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e0e3e5' }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={submitting}>Salvar</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Page ── */

export function ChartOfAccountsPage(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>(undefined);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const ctx = useAccountingCtx();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const { data: accounts, isLoading, error } = useChartOfAccounts(ctx);

  /* Filtro sobre a arvore: uma conta entra se ela casa, OU se alguma
     descendente casa — senao filtrar por nome de conta folha esconderia o
     caminho ate ela e a arvore ficaria vazia. */
  const treeMatches = useCallback(
    (account: Account): Account | null => {
      const term = search.trim().toLowerCase();
      const kept = (account.children ?? [])
        .map(treeMatches)
        .filter((c): c is Account => c !== null);
      const selfMatch =
        (!term ||
          account.code.toLowerCase().includes(term) ||
          account.name.toLowerCase().includes(term)) &&
        (typeFilter === 'ALL' || account.type === typeFilter);
      if (!selfMatch && kept.length === 0) return null;
      return { ...account, children: kept.length > 0 ? kept : account.children };
    },
    [search, typeFilter],
  );

  const visibleAccounts = (accounts ?? [])
    .map(treeMatches)
    .filter((a): a is Account => a !== null);
  const createMutation = useCreateAccount(ctx);
  const updateMutation = useUpdateAccount();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: AccountInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Conta criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar conta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: AccountInput) => {
      if (!editingAccount) return;
      try {
        await updateMutation.mutateAsync({ id: editingAccount.id, ...input });
        showToast('Conta atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar conta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingAccount, updateMutation, showToast],
  );

  const handleEdit = useCallback((acc: Account) => {
    setEditingAccount(acc);
    setModalOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingAccount(undefined);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingAccount(undefined);
  }, []);

  const safeAccounts = accounts ?? [];

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Page header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Plano de Contas
          <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
            ({safeAccounts.length} contas)
          </span>
        </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Plano de contas contábil e sua hierarquia.</p>
        </div>
        <Button variant="primary" onClick={handleNew}>+ Nova Conta</Button>
      </div>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <div className="flex flex-wrap items-center gap-sm">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar conta ou código..."
                aria-label="Buscar conta"
                className="bg-surface border border-outline-variant font-body-sm text-body-sm text-on-surface rounded py-1.5 px-sm focus:ring-1 focus:ring-primary focus:border-primary w-full sm:w-72"
              />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filtrar por tipo"
                className="bg-surface border border-outline-variant font-body-sm text-body-sm text-on-surface rounded py-1.5 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
              >
                <option value="ALL">Todos os tipos</option>
                {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Plano de contas</h3>
        </CardHeader>
        {/* Loading */}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando plano de contas...</span>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar plano de contas</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && safeAccounts.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma conta cadastrada.</p>
            <Button variant="outline" size="sm" onClick={handleNew}>Criar primeira conta</Button>
          </div>
        )}

        {/* Tree table */}
        {!isLoading && !error && safeAccounts.length > 0 && visibleAccounts.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p className="font-body-md text-body-md text-on-surface-variant m-0">
              Nenhuma conta corresponde ao filtro.
            </p>
          </div>
        )}

        {!isLoading && !error && visibleAccounts.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Código', 'Nome', 'Tipo', 'Natureza', 'Status', 'Saldo', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Saldo' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map((acc) => (
                  <AccountTreeNode key={acc.id} account={acc} depth={0} onEdit={handleEdit} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Toast */}
      {toastMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
            animation: 'toast-slide-in 0.3s ease',
          }}
        >
          {toastMessage}
          <button
            onClick={() => setToastMessage(null)}
            aria-label="Fechar"
            style={{
              marginLeft: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '1rem',
              padding: '0',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Modal */}
      <AccountFormModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        account={editingAccount}
        accounts={safeAccounts}
        onSubmit={editingAccount ? handleUpdate : handleCreate}
      />

      <style>{`
        .chart-row:hover td { background: #eceef0; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
